/**
 * Admin RBAC matrix — role defaults for all users of a role, or per-user overrides.
 *
 * - Read-only until Admin clicks Edit.
 * - No user selected: editing a column updates that role for everyone with that Users role.
 * - User selected: their role column is highlighted; only that column is editable;
 *   save writes personal overrides (role unchanged).
 */
import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Put } from '@/lib/api';
import { useHasPermission, useMyPermissions } from '@/auth/permissions';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Lock, Save, Pencil, X, UserRound, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { classNames } from '@/lib/format';

const NONE_USER = '__none__';

/** Catalog codes that are not product permission surfaces — keep grants/enforcement, hide from matrix. */
const MATRIX_HIDDEN_PERMISSION_CODES = new Set([
  'analytics.view', // manager-proxy gate only; no Analytics feature
  'report.download', // unused; AI report download is not gated by this code
]);

const ROLE_LABELS = {
  admin: 'Admin',
  hr_manager: 'HR Manager',
  hr_recruiter: 'HR Recruiter',
  hiring_manager: 'Hiring Manager',
  interviewer: 'Interviewer',
};

function cellOverrideKind(userEffective, roleCode, highlightedRoleCode, permCode, checked) {
  if (!userEffective || roleCode !== highlightedRoleCode) return null;
  const baseline = new Set(userEffective.role_permission_codes || []);
  if (checked && !baseline.has(permCode)) return 'grant';
  if (!checked && baseline.has(permCode)) return 'revoke';
  return null;
}

export default function RbacMatrix() {
  const qc = useQueryClient();
  const { data: myPerms, isLoading: permsLoading } = useMyPermissions();
  const canEditRbac = useHasPermission('admin.rbac.manage');
  const isAdminEffective =
    myPerms?.legacy_role === 'admin' ||
    (myPerms?.roles || []).some((r) => r.code === 'admin');
  const granted = new Set(myPerms?.permission_codes || []);
  const canViewRbac =
    isAdminEffective || granted.has('admin.rbac.view') || granted.has('admin.rbac.manage');

  const [editMode, setEditMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(NONE_USER);
  const [userFilter, setUserFilter] = useState('');
  const [rolePerms, setRolePerms] = useState({});
  const [rolePermsReady, setRolePermsReady] = useState(false);
  const [userEffective, setUserEffective] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [dirtyRoleIds, setDirtyRoleIds] = useState(() => new Set());
  const [userDirty, setUserDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['rbac-roles'],
    queryFn: () => v1Get('/admin/rbac/roles'),
    enabled: canViewRbac,
  });
  const { data: permissions = [], isLoading: permissionsLoading } = useQuery({
    queryKey: ['rbac-permissions'],
    queryFn: () => v1Get('/admin/rbac/permissions'),
    enabled: canViewRbac,
  });
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users-list'],
    queryFn: () => v1Get('/users'),
    enabled: canViewRbac,
  });

  const selectedUser = useMemo(
    () => (selectedUserId === NONE_USER ? null : users.find((u) => u.id === selectedUserId) || null),
    [selectedUserId, users],
  );
  const highlightedRoleCode = selectedUser?.role || null;
  const selectedIsAdmin = selectedUser?.role === 'admin';
  const dirty = selectedUser ? userDirty : dirtyRoleIds.size > 0;

  const filteredUsers = useMemo(() => {
    const q = userFilter.trim().toLowerCase();
    const active = users.filter((u) => u.status !== 'disabled');
    if (!q) return active;
    return active.filter(
      (u) =>
        (u.full_name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.role || '').toLowerCase().includes(q),
    );
  }, [users, userFilter]);

  const loadRolePerms = React.useCallback(async () => {
    if (roles.length === 0) {
      setRolePerms({});
      setRolePermsReady(false);
      return;
    }
    const pairs = await Promise.all(
      roles.map((r) => v1Get(`/admin/rbac/roles/${r.id}/permissions`).then((codes) => [r.id, codes])),
    );
    const map = {};
    pairs.forEach(([id, codes]) => {
      map[id] = new Set(codes);
    });
    setRolePerms(map);
    setRolePermsReady(true);
    setDirtyRoleIds(new Set());
  }, [roles]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (roles.length === 0) return;
      setRolePermsReady(false);
      try {
        const pairs = await Promise.all(
          roles.map((r) =>
            v1Get(`/admin/rbac/roles/${r.id}/permissions`).then((codes) => [r.id, codes]),
          ),
        );
        if (cancelled) return;
        const map = {};
        pairs.forEach(([id, codes]) => {
          map[id] = new Set(codes);
        });
        setRolePerms(map);
        setRolePermsReady(true);
        setDirtyRoleIds(new Set());
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || 'Failed to load role permissions');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [roles]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedUser) {
        setUserEffective(null);
        setUserDirty(false);
        setUserLoading(false);
        return;
      }
      setUserLoading(true);
      try {
        const detail = await v1Get(`/admin/rbac/users/${selectedUser.id}/effective-permissions`);
        if (!cancelled) {
          setUserEffective(detail);
          setUserDirty(false);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || 'Failed to load user permissions');
          setUserEffective(null);
        }
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedUser]);

  const permissionsByCategory = useMemo(() => {
    const grouped = {};
    permissions.forEach((p) => {
      if (MATRIX_HIDDEN_PERMISSION_CODES.has(p.code)) return;
      grouped[p.category] = grouped[p.category] || [];
      grouped[p.category].push(p);
    });
    return grouped;
  }, [permissions]);

  const overrideSummary = useMemo(() => {
    if (!userEffective || selectedIsAdmin) return null;
    const g = (userEffective.override_grants || []).length;
    const r = (userEffective.override_revokes || []).length;
    if (!g && !r && !userDirty) return null;
    return { grants: g, revokes: r };
  }, [userEffective, selectedIsAdmin, userDirty]);

  const cellChecked = (role, permCode) => {
    if (role.code === 'admin') return true;
    if (selectedUser && role.code === highlightedRoleCode && userEffective) {
      return (userEffective.effective_codes || []).includes(permCode);
    }
    return rolePerms[role.id]?.has(permCode) ?? false;
  };

  const toggle = (role, permCode) => {
    if (!editMode || !canEditRbac || role.code === 'admin') return;

    if (selectedUser) {
      if (role.code !== highlightedRoleCode || selectedIsAdmin) return;
      setUserEffective((prev) => {
        if (!prev) return prev;
        const set = new Set(prev.effective_codes || []);
        if (set.has(permCode)) set.delete(permCode);
        else set.add(permCode);
        return { ...prev, effective_codes: Array.from(set) };
      });
      setUserDirty(true);
      return;
    }

    setRolePerms((prev) => {
      const next = { ...prev };
      const set = new Set(next[role.id] || []);
      if (set.has(permCode)) set.delete(permCode);
      else set.add(permCode);
      next[role.id] = set;
      return next;
    });
    setDirtyRoleIds((prev) => {
      const next = new Set(prev);
      next.add(role.id);
      return next;
    });
  };

  const exitEdit = async ({ reload = true } = {}) => {
    setEditMode(false);
    setUserDirty(false);
    setDirtyRoleIds(new Set());
    if (reload) {
      await loadRolePerms();
      if (selectedUser) {
        setUserLoading(true);
        try {
          const detail = await v1Get(`/admin/rbac/users/${selectedUser.id}/effective-permissions`);
          setUserEffective(detail);
        } finally {
          setUserLoading(false);
        }
      }
    }
  };

  const save = async () => {
    if (!canEditRbac || !editMode || !dirty) return;
    setSaving(true);
    try {
      if (selectedUser) {
        if (selectedIsAdmin) {
          toast.error('Admin users always have all permissions');
          return;
        }
        if (!userEffective) {
          toast.error('User permissions are still loading');
          return;
        }
        const saved = await v1Put(
          `/admin/rbac/users/${selectedUser.id}/permission-overrides`,
          { permission_codes: userEffective.effective_codes || [] },
        );
        setUserEffective(saved);
        setUserDirty(false);
        toast.success('Personal permissions saved');
      } else {
        if (!rolePermsReady) {
          toast.error('Role permissions are still loading');
          return;
        }
        const ids = Array.from(dirtyRoleIds);
        await Promise.all(
          ids.map((roleId) =>
            v1Put(`/admin/rbac/roles/${roleId}/permissions`, {
              permission_codes: Array.from(rolePerms[roleId] || []),
            }),
          ),
        );
        setDirtyRoleIds(new Set());
        toast.success(
          ids.length === 1
            ? 'Role permissions saved'
            : `Updated ${ids.length} roles`,
        );
      }
      await qc.invalidateQueries({ queryKey: ['my-permissions'] });
      setEditMode(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onSelectUser = (value) => {
    if (editMode && dirty) {
      toast.message('Save or cancel your edits before switching user');
      return;
    }
    setSelectedUserId(value);
    setEditMode(false);
    setUserDirty(false);
    setDirtyRoleIds(new Set());
  };

  const startEdit = () => {
    if (selectedIsAdmin) {
      toast.message('Admin always has every permission — nothing to edit');
      return;
    }
    if (selectedUser && userLoading) {
      toast.message('Still loading this user’s permissions');
      return;
    }
    if (!selectedUser && !rolePermsReady) {
      toast.message('Still loading role permissions');
      return;
    }
    setEditMode(true);
  };

  if (!permsLoading && !canViewRbac) {
    return <Navigate to="/portal" replace />;
  }

  const matrixLoading = rolesLoading || permissionsLoading || (!rolePermsReady && roles.length > 0);
  const modeBanner = selectedUser
    ? selectedIsAdmin
      ? `${selectedUser.full_name || selectedUser.email} is Admin — all permissions are locked on.`
      : `Personal overrides for ${selectedUser.full_name || selectedUser.email} · role ${ROLE_LABELS[selectedUser.role] || selectedUser.role} (unchanged)`
    : 'Role defaults — changes apply to every user with that role';

  return (
    <div className="space-y-5" data-testid="rbac-matrix-page">
      <PageHeader
        title="Roles & Permissions"
        subtitle={
          canEditRbac
            ? 'Edit role columns for everyone in that role, or pick one person for personal exceptions without changing their role.'
            : 'View-only — ask an Admin to change permissions.'
        }
      />

      {!canEditRbac && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="rbac-readonly-banner"
        >
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Lock className="h-4 w-4" /> View only
          </span>
          <span className="ml-2 text-amber-800">
            You can review the matrix but cannot save changes.
          </span>
        </div>
      )}

      <Card className="card-elevated">
        <CardContent className="space-y-3 p-4" data-testid="rbac-toolbar">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Scope
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <UserRound className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  placeholder="Filter users…"
                  className="h-9 w-[160px]"
                  disabled={usersLoading}
                  data-testid="rbac-user-filter"
                />
                <Select value={selectedUserId} onValueChange={onSelectUser}>
                  <SelectTrigger className="w-[280px]" data-testid="rbac-user-select">
                    <SelectValue placeholder="All users (role defaults)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_USER}>All users (role defaults)</SelectItem>
                    {filteredUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name || u.email}
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({ROLE_LABELS[u.role] || u.role})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2">
              {canEditRbac && !editMode && (
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={startEdit}
                  disabled={selectedIsAdmin || matrixLoading || (selectedUser && userLoading)}
                  data-testid="rbac-edit-button"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              )}
              {canEditRbac && editMode && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => exitEdit({ reload: true })}
                    disabled={saving}
                    data-testid="rbac-cancel-button"
                  >
                    <X className="h-3.5 w-3.5" /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={save}
                    disabled={saving || !dirty}
                    data-testid="rbac-save-button"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div
            className={classNames(
              'flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs',
              editMode ? 'border-primary/30 bg-primary/5 text-foreground' : 'bg-secondary/40 text-muted-foreground',
            )}
            data-testid="rbac-mode-label"
          >
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">
              {editMode ? `Editing · ${modeBanner}` : modeBanner}
              {!editMode && ' · Matrix is read-only until you click Edit.'}
            </span>
            {overrideSummary && (
              <Badge variant="secondary" className="gap-1 font-normal" data-testid="rbac-override-badge">
                {overrideSummary.grants} extra · {overrideSummary.revokes} removed
              </Badge>
            )}
            {dirty && editMode && (
              <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100" data-testid="rbac-dirty-badge">
                Unsaved changes
              </Badge>
            )}
          </div>

          {selectedUser && !selectedIsAdmin && (
            <p className="text-[11px] text-muted-foreground">
              Highlighted column = this person’s effective access.
              <span className="ml-2 inline-flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" /> extra grant
              </span>
              <span className="ml-2 inline-flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> revoked vs role
              </span>
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardContent className="p-0" data-testid="rbac-matrix">
          {matrixLoading || (selectedUser && userLoading) ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" data-testid="rbac-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading permissions…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-card shadow-sm">
                  <tr className="border-b">
                    <th className="sticky left-0 z-20 bg-card p-3 text-left font-semibold text-muted-foreground">
                      Permission
                    </th>
                    {roles.map((r) => {
                      const highlighted = highlightedRoleCode === r.code;
                      const colDirty = dirtyRoleIds.has(r.id);
                      return (
                        <th
                          key={r.id}
                          className={classNames(
                            'px-3 py-3 text-center font-semibold whitespace-nowrap',
                            highlighted && 'bg-primary/10',
                          )}
                          data-testid={`rbac-col-${r.code}`}
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <span>{r.name}</span>
                            {r.code === 'admin' && (
                              <span className="text-[10px] font-normal text-muted-foreground">locked</span>
                            )}
                            {highlighted && (
                              <span className="text-[10px] font-normal text-primary">selected user</span>
                            )}
                            {colDirty && editMode && !selectedUser && (
                              <span className="text-[10px] font-normal text-amber-700">edited</span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(permissionsByCategory).map(([category, perms]) => (
                    <React.Fragment key={category}>
                      <tr className="bg-secondary/50">
                        <td
                          colSpan={roles.length + 1}
                          className="sticky left-0 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                          data-testid={`rbac-category-${category}`}
                        >
                          {category}
                        </td>
                      </tr>
                      {perms.map((p) => (
                        <tr key={p.code} className="border-b hover:bg-accent/40" data-testid={`rbac-row-${p.code}`}>
                          <td className="sticky left-0 z-10 bg-card p-3">
                            <p className="font-medium">{p.name}</p>
                            {p.description && (
                              <p className="mt-0.5 text-[11px] italic text-muted-foreground">
                                {p.description}
                              </p>
                            )}
                          </td>
                          {roles.map((r) => {
                            const isAdmin = r.code === 'admin';
                            const highlighted = highlightedRoleCode === r.code;
                            const inUserMode = !!selectedUser;
                            const columnEditable =
                              editMode
                              && canEditRbac
                              && !isAdmin
                              && !selectedIsAdmin
                              && (!inUserMode || highlighted);
                            const checked = cellChecked(r, p.code);
                            const kind = cellOverrideKind(
                              userEffective,
                              r.code,
                              highlightedRoleCode,
                              p.code,
                              checked,
                            );
                            return (
                              <td
                                key={r.id}
                                className={classNames(
                                  'px-3 py-2 text-center',
                                  highlighted && 'bg-primary/5',
                                  inUserMode && !highlighted && !isAdmin && 'opacity-40',
                                )}
                              >
                                <div className="relative inline-flex items-center justify-center">
                                  <Checkbox
                                    checked={checked}
                                    disabled={!columnEditable}
                                    onCheckedChange={() => columnEditable && toggle(r, p.code)}
                                    className={classNames(
                                      kind === 'grant' && 'border-emerald-500 data-[state=checked]:bg-emerald-600',
                                      kind === 'revoke' && 'border-amber-500',
                                    )}
                                    data-testid={`rbac-cell-${r.code}-${p.code}`}
                                  />
                                  {kind === 'grant' && (
                                    <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                  )}
                                  {kind === 'revoke' && (
                                    <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-amber-500" />
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
